import { LoadingIndicatorComponent } from '../../components/loading-indicator/loading-indicator.component';
import { Component, ElementRef, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import {
  AerosysGatewayService,
  AerosysItemConferenciaLegado,
  AerosysPreviewDesmembramento,
  AerosysUsuarioSeparador
} from '../../services/aerosys-gateway.service';

/**
 * ============================================================================
 *  INTEGRAÇÃO GPL — Conferência (cega) de vendas do Sistema Legado (Aerosys)
 * ============================================================================
 *  Tela de "Continuar Conferência" para vendas do Aerosys — mirror de
 *  EstoqueSeparacaoItensLegadoComponent, adaptado pro fluxo de Conferência
 *  (ver ConferenciaLegadoService no projeto Aerosys, pacote
 *  com.infoaries.aerosys.gplconferencia).
 *
 *  Diferenças deliberadas em relação à versão Separação:
 *   - SEM validação de posse: qualquer usuário identificado pode continuar
 *     uma conferência já iniciada por outra pessoa (mesma decisão do
 *     cliente aplicada à Separação).
 *   - SEM os recursos de Dividir/Transferir (o backend não tem esse
 *     conceito pra Conferência — no máximo 1 sessão ativa por venda).
 *   - SEM campo de TAG na identificação: a Conferência não usa TAG física
 *     (isso é só da Separação) — o Aerosys só precisa saber o codusu de
 *     quem está conferindo (ver ConferenciaLegadoController.IniciarRequest,
 *     que só tem "codusu", sem "tag").
 *   - Confirmação é por QUANTIDADE, não um toggle binário: o conferente
 *     digita quanto encontrou (por padrão, a quantidade pendente inteira).
 *     Se digitar MENOS que a pendente, a venda vai DESMEMBRAR essa linha
 *     (ver comentário de topo de ConferenciaLegadoService) — antes de
 *     gravar, pedimos uma prévia (previewDesmembramentoConferenciaLegado) e
 *     mostramos um modal de confirmação com o "antes/depois" (pedido
 *     explícito do cliente), só chamando confirmarContagemConferenciaLegado
 *     de verdade depois que o conferente confirmar ali.
 *   - Finalizar é sempre possível manualmente (não só quando 100% concluído)
 *     — pedido do cliente: mesmo conferindo só 1 de vários itens, o
 *     conferente pode finalizar e o backend decide sozinho o status final
 *     (PRONTO se tudo foi atendido, ESTOQUE se sobrou pendência — ver
 *     ConferenciaLegadoService#finalizar). O banner automático (aparece
 *     quando os itens acabam) continua existindo pro caminho comum.
 *
 *  Isolamento: este componente é standalone e não é referenciado por
 *  nenhuma tela do fluxo Sankhya — pode ser removido (pasta inteira) sem
 *  tocar em mais nada, junto com o pacote gplconferencia no backend Aerosys
 *  e os métodos *ConferenciaLegado* do AerosysGatewayService/Repository/
 *  Component/Controller, quando o Aerosys for desativado.
 * ============================================================================
 */
@Component({
  selector: 'app-estoque-conferencia-itens-legado',
  standalone: true,
  imports: [LoadingIndicatorComponent, CommonModule, FormsModule],
  templateUrl: './estoque-conferencia-itens-legado.component.html',
  styleUrls: ['./estoque-conferencia-itens-legado.component.css']
})
export class EstoqueConferenciaItensLegadoComponent implements OnInit {
  @Input() idVenda!: number;
  @Input() numVenda?: string;
  @Output() voltar = new EventEmitter<void>();
  @Output() finalizado = new EventEmitter<void>();

  itens: AerosysItemConferenciaLegado[] = [];
  carregando = false;
  erro = '';
  erroSalvar = '';

  abaAtiva: 'pendentes' | 'conferidos' = 'pendentes';

  finalizando = false;
  erroFinalizar = '';
  modalFinalizar = false;

  // ---------------------------------------------------------------------
  // Identificação "quem sou você" — só usuário (sem TAG, ver comentário de
  // topo). Precisa ser resolvida ANTES de carregar os itens.
  // ---------------------------------------------------------------------
  identificado = false;
  identificando = false;
  /** true enquanto tenta reaproveitar em silêncio uma identificação já feita antes NESTA venda. */
  restaurandoIdentificacao = false;
  buscaUsuario = '';
  usuariosSugeridos: AerosysUsuarioSeparador[] = [];
  buscandoUsuarios = false;
  usuarioEscolhido: AerosysUsuarioSeparador | null = null;
  erroIdentificacao = '';
  codusuAtual: number | null = null;
  nomeAtual: string | null = null;
  private buscaUsuarioTimer: any;

  /** Só o primeiro nome -- pro badge do header caber na mesma linha. */
  primeiroNome(nomeCompleto: string | null): string {
    if (!nomeCompleto) return '';
    return nomeCompleto.trim().split(/\s+/)[0];
  }

  // ---------------------------------------------------------------------
  // Confirmação de quantidade (item atual, ver itemPendenteAtual) + preview
  // de desmembramento (quando a quantidade digitada é MENOR que a pendente).
  // ---------------------------------------------------------------------
  /** ivdId do item cujo rascunho de quantidade está em quantidadeDraft -- evita reaproveitar o rascunho de um item diferente ao trocar de item atual. */
  private draftIvdId: number | null = null;
  quantidadeDraft: number | null = null;
  confirmando = false;
  erroConfirmar = '';

  modalDesmembramento = false;
  carregandoPreview = false;
  erroPreview = '';
  previewResultado: AerosysPreviewDesmembramento | null = null;

  // ---------------------------------------------------------------------
  // Modal "Item no outro Galpão" -- mesmo padrão da Separação.
  // ---------------------------------------------------------------------
  itemPendencia: AerosysItemConferenciaLegado | null = null;
  modalPendencia = false;
  pendenciaObservacaoRascunho = '';
  marcandoPendente = false;
  erroPendencia = '';
  desfazendoPendenciaIvdId: number | null = null;

  constructor(private gateway: AerosysGatewayService) {}

  ngOnInit(): void {
    const lembrada = this._restaurarIdentificacao();
    if (lembrada) {
      this.restaurandoIdentificacao = true;
      this._iniciar(lembrada.codusu, lembrada.nome, false);
      return;
    }
    // Sem identificação lembrada -- mostra o modal "quem sou você".
  }

  /**
   * Extrai uma mensagem de erro legível de uma resposta HTTP do gateway.
   * Ver comentário equivalente em EstoqueSeparacaoItensLegadoComponent.
   */
  private extrairErro(err: any, fallback: string): string {
    const corpo = err?.error;
    if (typeof corpo === 'string' && corpo.trim()) return corpo;
    if (corpo?.erro) return corpo.erro;
    if (corpo?.detail) return corpo.detail;
    if (corpo?.message) return corpo.message;
    if (err?.status === 0) return 'Não foi possível conectar ao servidor.';
    return err?.message ?? fallback;
  }

  // =======================================================================
  // Identificação "quem sou você"
  // =======================================================================

  onBuscaUsuarioChange(): void {
    clearTimeout(this.buscaUsuarioTimer);
    const termo = this.buscaUsuario.trim();
    if (termo.length < 2) {
      this.usuariosSugeridos = [];
      this.buscandoUsuarios = false;
      return;
    }
    this.buscaUsuarioTimer = setTimeout(() => this.buscarUsuarios(termo), 300);
  }

  private buscarUsuarios(termo: string): void {
    this.buscandoUsuarios = true;
    this.erroIdentificacao = '';
    this.gateway.buscarUsuariosConferenciaLegado(termo).subscribe({
      next: (usuarios) => {
        this.usuariosSugeridos = usuarios ?? [];
        this.buscandoUsuarios = false;
      },
      error: (err: any) => {
        this.usuariosSugeridos = [];
        this.buscandoUsuarios = false;
        this.erroIdentificacao = this.extrairErro(err, 'Erro ao buscar usuários do Aerosys.');
      }
    });
  }

  escolherUsuario(usuario: AerosysUsuarioSeparador): void {
    this.usuarioEscolhido = usuario;
    this.buscaUsuario = usuario.nome ?? '';
    this.usuariosSugeridos = [];
  }

  limparUsuarioEscolhido(): void {
    this.usuarioEscolhido = null;
    this.buscaUsuario = '';
    this.usuariosSugeridos = [];
  }

  confirmarIdentificacao(): void {
    this.erroIdentificacao = '';
    if (!this.usuarioEscolhido) {
      this.erroIdentificacao = 'Selecione o seu usuário na lista antes de continuar.';
      return;
    }
    this.identificando = true;
    this._iniciar(this.usuarioEscolhido.codusu, this.usuarioEscolhido.nome ?? null, true);
  }

  /**
   * Chama o "iniciar" de verdade (idempotente -- se já existe uma sessão
   * ativa nesta venda, o backend só devolve os itens, ver
   * ConferenciaLegadoService#iniciar) e, se pedido, lembra a identificação
   * (codusu/nome) pra essa venda no localStorage.
   */
  private _iniciar(codusu: number, nome: string | null, persistir: boolean): void {
    this.gateway.iniciarConferenciaLegado(this.idVenda, codusu).subscribe({
      next: (itens) => {
        this.identificando = false;
        this.restaurandoIdentificacao = false;
        this.codusuAtual = codusu;
        this.nomeAtual = nome;
        this.identificado = true;
        this.itens = itens ?? [];
        this._sincronizarDraftComItemAtual();
        if (persistir) this._salvarIdentificacao(codusu, nome);
      },
      error: (err: any) => {
        this.identificando = false;
        if (this.restaurandoIdentificacao) {
          this.restaurandoIdentificacao = false;
          this._removerIdentificacaoLembrada();
          return;
        }
        this.erroIdentificacao = this.extrairErro(err, 'Erro ao iniciar a conferência.');
      }
    });
  }

  /** Esquece a identificação lembrada desta venda e volta a pedir usuário -- "não sou eu" / troca de turno. */
  trocarIdentificacao(): void {
    this._removerIdentificacaoLembrada();
    this.identificado = false;
    this.codusuAtual = null;
    this.nomeAtual = null;
    this.usuarioEscolhido = null;
    this.buscaUsuario = '';
    this.itens = [];
  }

  private _storageKeyIdentificacao(): string {
    return `gpl_estoque_conferencia_legado_identificacao_${this.idVenda}`;
  }

  private _salvarIdentificacao(codusu: number, nome: string | null): void {
    try {
      localStorage.setItem(this._storageKeyIdentificacao(), JSON.stringify({ codusu, nome }));
    } catch {
      // localStorage indisponível (ex.: navegação privada) -- só perde a conveniência de não perguntar de novo.
    }
  }

  private _restaurarIdentificacao(): { codusu: number; nome: string | null } | null {
    try {
      const raw = localStorage.getItem(this._storageKeyIdentificacao());
      if (!raw) return null;
      const obj = JSON.parse(raw);
      if (typeof obj?.codusu !== 'number') return null;
      return { codusu: obj.codusu, nome: obj.nome ?? null };
    } catch {
      return null;
    }
  }

  private _removerIdentificacaoLembrada(): void {
    try {
      localStorage.removeItem(this._storageKeyIdentificacao());
    } catch {
      // ignora
    }
  }

  // =======================================================================
  // Itens da conferência
  // =======================================================================

  onVoltar(): void {
    this.voltar.emit();
  }

  /**
   * Fila "A Conferir" -- itens ainda com IVD_SAIDA='N' e sem pendência
   * aberta. Igual à Separação, revelamos só o PRÓXIMO item por vez (ver
   * itemPendenteAtual) -- pedido do cliente pra o conferente ir item a
   * item, sem se distrair com o resto da lista.
   */
  get itensPendentes(): AerosysItemConferenciaLegado[] {
    return this.itens.filter(i => i.saida !== 'S' && !i.pendente);
  }

  get itemPendenteAtual(): AerosysItemConferenciaLegado[] {
    const pendentes = this.itensPendentes;
    return pendentes.length > 0 ? [pendentes[0]] : [];
  }

  /** Itens efetivamente conferidos OU sinalizados como pendentes ("Item no outro Galpão"). */
  get itensConferidosLista(): AerosysItemConferenciaLegado[] {
    return this.itens.filter(i => i.saida === 'S' || i.pendente);
  }

  get totalConferidos(): number {
    return this.itens.filter(i => i.saida === 'S').length;
  }

  get totalPendentesAbertas(): number {
    return this.itens.filter(i => !!i.pendente).length;
  }

  get progresso(): number {
    if (!this.itens.length) return 0;
    return Math.round((this.totalConferidos / this.itens.length) * 100);
  }

  /** "Tudo pronto" -- ESTRITO: todo item precisa estar com saida='S' de verdade (pendências sinalizadas não contam, mesmo espírito da Separação). */
  get allDone(): boolean {
    return this.itens.length > 0 && this.itens.every(i => i.saida === 'S');
  }

  /**
   * Garante que quantidadeDraft sempre reflete o item atualmente exibido na
   * fila "A Conferir" -- reseta pro valor pendente (quantidade cheia) toda
   * vez que o item atual muda (após carregar a lista ou confirmar o item
   * anterior, que "revela" o próximo).
   */
  private _sincronizarDraftComItemAtual(): void {
    const atual = this.itemPendenteAtual[0];
    if (!atual) {
      this.draftIvdId = null;
      this.quantidadeDraft = null;
      return;
    }
    if (this.draftIvdId !== atual.ivdId) {
      this.draftIvdId = atual.ivdId;
      this.quantidadeDraft = atual.quantidade ?? null;
    }
  }

  /** Clique em "Confirmar" no card do item atual -- decide se desmembra (pede prévia) ou confirma direto. */
  confirmarQuantidade(): void {
    const item = this.itemPendenteAtual[0];
    if (!item || this.confirmando || this.codusuAtual == null) return;
    this.erroConfirmar = '';

    const qtd = this.quantidadeDraft;
    const pendente = item.quantidade ?? 0;
    if (qtd == null || isNaN(qtd) || qtd <= 0) {
      this.erroConfirmar = 'Informe uma quantidade maior que zero.';
      return;
    }
    if (qtd > pendente + 0.0001) {
      this.erroConfirmar = `Quantidade informada (${qtd}) maior que a pendente (${pendente}).`;
      return;
    }

    const desmembra = qtd < pendente - 0.0001;
    if (!desmembra) {
      this._confirmarContagem(item, qtd);
      return;
    }

    // Vai desmembrar -- pede a prévia antes de gravar (pedido do cliente).
    this.carregandoPreview = true;
    this.erroPreview = '';
    this.gateway.previewDesmembramentoConferenciaLegado(this.idVenda, item.ivdId, qtd).subscribe({
      next: (preview) => {
        this.previewResultado = preview;
        this.carregandoPreview = false;
        this.modalDesmembramento = true;
      },
      error: (err: any) => {
        this.carregandoPreview = false;
        this.erroConfirmar = this.extrairErro(err, 'Erro ao calcular a prévia do desmembramento.');
      }
    });
  }

  fecharModalDesmembramento(): void {
    if (this.confirmando) return;
    this.modalDesmembramento = false;
    this.previewResultado = null;
    this.erroPreview = '';
  }

  /** Confirma o desmembramento de verdade -- chamado a partir do modal de prévia. */
  confirmarDesmembramento(): void {
    const item = this.itemPendenteAtual[0];
    if (!item || this.quantidadeDraft == null) return;
    this._confirmarContagem(item, this.quantidadeDraft, true);
  }

  private _confirmarContagem(item: AerosysItemConferenciaLegado, qtd: number, doModal = false): void {
    if (this.codusuAtual == null) return;
    this.confirmando = true;
    this.erroConfirmar = '';
    this.gateway.confirmarContagemConferenciaLegado(this.idVenda, item.ivdId, qtd, this.codusuAtual).subscribe({
      next: (itensAtualizados) => {
        this.itens = itensAtualizados ?? this.itens;
        this.confirmando = false;
        if (doModal) {
          this.modalDesmembramento = false;
          this.previewResultado = null;
        }
        this._sincronizarDraftComItemAtual();
        if (this.itensPendentes.length === 0) {
          this.abaAtiva = 'conferidos';
          if (this.allDone) {
            this.modalFinalizar = true;
          }
        }
      },
      error: (err: any) => {
        this.confirmando = false;
        const msg = this.extrairErro(err, 'Erro ao confirmar a contagem.');
        if (doModal) {
          this.erroPreview = msg;
        } else {
          this.erroConfirmar = msg;
        }
      }
    });
  }

  /** Botão "Finalizar Conferência" do header -- disponível manualmente a qualquer momento (com pelo menos 1 item conferido), não só quando allDone (ver comentário de topo da classe). */
  abrirModalFinalizar(): void {
    if (this.totalConferidos === 0) return;
    this.modalFinalizar = true;
  }

  finalizarDoModal(): void {
    this.modalFinalizar = false;
    this.finalizar();
  }

  finalizar(): void {
    if (this.finalizando || this.codusuAtual == null) return;
    this.finalizando = true;
    this.erroFinalizar = '';
    this.gateway.finalizarConferenciaLegado(this.idVenda, this.codusuAtual).subscribe({
      next: () => {
        this.finalizando = false;
        this.finalizado.emit();
      },
      error: (err: any) => {
        this.finalizando = false;
        this.erroFinalizar = this.extrairErro(err, 'Falha ao finalizar conferência.');
      }
    });
  }

  // =======================================================================
  // "Item no outro Galpão" -- sinalizar/desfazer pendência (mesmo padrão da
  // Separação). Resolver de verdade é feito na tela global "Itens
  // Pendentes", não aqui.
  // =======================================================================

  abrirModalPendencia(item: AerosysItemConferenciaLegado, event: Event): void {
    event.stopPropagation();
    this.itemPendencia = item;
    this.pendenciaObservacaoRascunho = '';
    this.erroPendencia = '';
    this.modalPendencia = true;
  }

  fecharModalPendencia(): void {
    if (this.marcandoPendente) return;
    this.modalPendencia = false;
    this.itemPendencia = null;
    this.pendenciaObservacaoRascunho = '';
  }

  confirmarPendencia(): void {
    if (this.marcandoPendente || this.codusuAtual == null || !this.itemPendencia) return;
    this.marcandoPendente = true;
    this.erroPendencia = '';
    const texto = this.pendenciaObservacaoRascunho.trim();
    this.gateway.marcarItemPendenteConferenciaLegado(this.idVenda, this.itemPendencia.ivdId, this.codusuAtual, texto || null).subscribe({
      next: (itemAtualizado) => {
        this._substituirItemNaLista(itemAtualizado);
        this.marcandoPendente = false;
        this.modalPendencia = false;
        this.itemPendencia = null;
        this.pendenciaObservacaoRascunho = '';
        this._sincronizarDraftComItemAtual();
        if (this.itensPendentes.length === 0) {
          this.abaAtiva = 'conferidos';
          if (this.allDone) {
            this.modalFinalizar = true;
          }
        }
      },
      error: (err: any) => {
        this.marcandoPendente = false;
        this.erroPendencia = this.extrairErro(err, 'Erro ao sinalizar pendência.');
      }
    });
  }

  /** Desfaz uma sinalização feita por engano -- item volta pra fila "A Conferir" (saida continua 'N', nunca chegou a mudar). */
  desfazerPendencia(item: AerosysItemConferenciaLegado, event: Event): void {
    event.stopPropagation();
    if (this.desfazendoPendenciaIvdId != null) return;
    this.desfazendoPendenciaIvdId = item.ivdId;
    this.erroSalvar = '';
    this.gateway.desfazerPendenciaConferenciaLegado(this.idVenda, item.ivdId).subscribe({
      next: (itemAtualizado) => {
        this._substituirItemNaLista(itemAtualizado);
        this.desfazendoPendenciaIvdId = null;
        this._sincronizarDraftComItemAtual();
        if (this.abaAtiva === 'conferidos' && this.itensPendentes.length > 0) {
          this.abaAtiva = 'pendentes';
        }
      },
      error: (err: any) => {
        this.desfazendoPendenciaIvdId = null;
        this.erroSalvar = this.extrairErro(err, 'Erro ao desfazer pendência.');
      }
    });
  }

  /** Substitui só 1 item na lista já carregada, pelo ivdId (marcar/desfazer pendência devolvem só o item que mudou). */
  private _substituirItemNaLista(itemAtualizado: AerosysItemConferenciaLegado): void {
    if (!itemAtualizado) return;
    this.itens = this.itens.map(i => (i.ivdId === itemAtualizado.ivdId ? itemAtualizado : i));
  }
}
